import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { mlModelAPI } from '../api';

const DemoContext = createContext();

export const useDemo = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
};

export const DemoProvider = ({ children }) => {
  const [isDemoRunning, setIsDemoRunning] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [demoData, setDemoData] = useState(null);
  const [demoModel, setDemoModel] = useState(null);
  const [demoError, setDemoError] = useState(null);
  const [isDemoComplete, setIsDemoComplete] = useState(false);

  // Pre-defined demo dataset configuration
  const demoConfig = useMemo(
    () => ({
      dataset: "cleaned_proteomics",
      targetColumn: "Integration of all omics data_Integrative rank",
      taskType: "classification",
      modelType: "random_forest",
      description: "Proteomics dataset with binary classification task",
      testSize: 0.2,
      normalize: false,
      normalizationMethod: null,
    }),
    []
  );

  const resetDemo = useCallback(() => {
    setIsDemoRunning(false);
    setDemoStep(0);
    setDemoData(null);
    setDemoModel(null);
    setDemoError(null);
    setIsDemoComplete(false);
  }, []);

  const startDemo = useCallback(async () => {
    try {
      setIsDemoRunning(true);
      setDemoError(null);
      setDemoStep(0);
      setIsDemoComplete(false);

      // Step 1: Load demo data info
      setDemoStep(1);
      setDemoData({
        datasetName: demoConfig.dataset,
        target_column: demoConfig.targetColumn,
        model_type: demoConfig.modelType,
        test_size: demoConfig.testSize,
        normalize: demoConfig.normalize,
        normalization_method: demoConfig.normalizationMethod,
        task_type: demoConfig.taskType,
      });

      // Broadcast selection so the dashboard mirrors demo config without manual setup
      window.dispatchEvent(new CustomEvent('demo:selectDataset', { detail: { dataset: demoConfig.dataset } }));
      window.dispatchEvent(new CustomEvent('demo:selectModel', { detail: { modelType: demoConfig.modelType } }));

      // Step 2: Train model
      setDemoStep(2);
      const trainingResponse = await mlModelAPI.trainMLModel(
        demoConfig.dataset, // datasetName
        demoConfig.targetColumn, // targetColumn
        demoConfig.modelType, // modelType
        demoConfig.testSize, // testSize
        demoConfig.normalize, // normalize
        demoConfig.normalizationMethod, // normalizationMethod
        demoConfig.taskType // taskType
      );

      console.log("trainingResponse", trainingResponse.status);
      if (trainingResponse.status === "success") {
        setDemoModel(trainingResponse.data);
        
        // Step 3: Wait a moment then mark as complete
        setTimeout(() => {
          setDemoStep(3);
          setIsDemoComplete(true);
        }, 2000);
      } else {
        throw new Error(trainingResponse.message || 'Training failed');
      }

    } catch (error) {
      console.error('Demo error:', error);
      setDemoError(error.message || 'Demo failed to run');
      setIsDemoRunning(false);
    }
  }, [demoConfig]);


  const value = {
    // State
    isDemoRunning,
    demoStep,
    demoData,
    demoModel,
    demoError,
    isDemoComplete,
    demoConfig,
    
    // Actions
    startDemo,
    resetDemo
  };

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
};
